import { useEffect, useMemo, useState } from "react";
import { hydrateFromCacheDeep, translateDeep } from "./translationApi";

const translatedCopyCache = new Map();
const RETRY_DELAYS_MS = [0, 250, 800];
function hasArabicInValue(value) {
  if (typeof value === "string") return /[\u0600-\u06FF]/.test(value);
  if (Array.isArray(value)) return value.some(hasArabicInValue);
  if (value && typeof value === "object") return Object.values(value).some(hasArabicInValue);
  return false;
}
function hasLatinLetters(value) {
  if (typeof value === "string") return /[A-Za-z]/.test(value);
  if (Array.isArray(value)) return value.some(hasLatinLetters);
  if (value && typeof value === "object") return Object.values(value).some(hasLatinLetters);
  return false;
}
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getSourceSignature(source) {
  if (typeof source === "string") return source;

  try {
    return JSON.stringify(source);
  } catch {
    return String(source);
  }
}

function normalizeArabicTerminology(value) {
  if (typeof value === "string") {
    return value.replace(/الممتلكات/g, "الأملاك");
  }

  if (Array.isArray(value)) {
    return value.map(normalizeArabicTerminology);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, normalizeArabicTerminology(item)])
    );
  }

  return value;
}

function resolveManualLocale(value, locale) {
  if (Array.isArray(value)) return value.map((item) => resolveManualLocale(item, locale));

  if (value && typeof value === "object") {
    const keys = Object.keys(value);
    const isExplicitLocaleObject =
      keys.length > 0 && keys.every((key) => key === "en" || key === "ar");

    if (isExplicitLocaleObject) {
      return value[locale] ?? value.en ?? value.ar ?? "";
    }

    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, resolveManualLocale(item, locale)])
    );
  }

  return value;
}

export function useTranslatedCopy(source, locale, cacheKey) {
  const sourceSignature = useMemo(() => getSourceSignature(source), [source]);
  const normalizedSource = useMemo(() => {
    const resolved = resolveManualLocale(source, locale);
    return locale === "ar" ? normalizeArabicTerminology(resolved) : resolved;
  }, [locale, sourceSignature]);
  const resolvedCacheKey = useMemo(
    () => (cacheKey ? `${locale}:${cacheKey}` : `${locale}:${sourceSignature}`),
    [locale, cacheKey, sourceSignature]
  );
  const immediateCopy = useMemo(() => {
    if (locale !== "ar") return normalizedSource;
    return normalizeArabicTerminology(hydrateFromCacheDeep(normalizedSource, "ar"));
  }, [locale, normalizedSource]);
  const [copy, setCopy] = useState(immediateCopy);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setCopy(immediateCopy);

      if (locale !== "ar") {
        return;
      }

      if (resolvedCacheKey && translatedCopyCache.has(resolvedCacheKey)) {
        const cached = normalizeArabicTerminology(translatedCopyCache.get(resolvedCacheKey));
        if (hasArabicInValue(cached) || !hasLatinLetters(normalizedSource)) {
          setCopy(cached);
          return;
        }
        translatedCopyCache.delete(resolvedCacheKey);
      }

      if (!hasLatinLetters(immediateCopy) || !hasLatinLetters(normalizedSource)) {
        if (resolvedCacheKey && hasArabicInValue(immediateCopy)) {
          translatedCopyCache.set(resolvedCacheKey, immediateCopy);
        }
        return;
      }

      try {
        let translated = normalizedSource;
        for (const delay of RETRY_DELAYS_MS) {
          if (delay > 0) await sleep(delay);
          translated = normalizeArabicTerminology(await translateDeep(normalizedSource, "ar"));
          if (hasArabicInValue(translated) || !hasLatinLetters(normalizedSource)) {
            break;
          }
        }

        if (!cancelled) {
          if (resolvedCacheKey && hasArabicInValue(translated)) {
            translatedCopyCache.set(resolvedCacheKey, translated);
          }
          setCopy(translated);
        }
      } catch {
        if (!cancelled) setCopy(immediateCopy);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [immediateCopy, locale, normalizedSource, resolvedCacheKey]);

  return copy;
}
